/**
 * 운영 도구 - 멤버십 가입
 * 가족 / 연인·친구 유형 선택 후 전화번호 입력으로 회원 등록
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StoreBadge from '../components/StoreBadge';
import { getMemberByPhone, registerMemberOnly, addChildMember, getFamilyMembers } from '../services/api';
import './MembershipRegisterPage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
// 단계: type → phone → confirm → done (+ children for family)
const MembershipRegisterPage = () => {
  const navigate = useNavigate();

  // 공통 상태
  const [mode, setMode] = useState(null); // 'family' | 'friends'
  const [step, setStep] = useState('type'); // type → phone → confirm → children → done
  const [phoneInput, setPhoneInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // 가족 모드
  const [parentMember, setParentMember] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [nickname, setNickname] = useState('');
  const [birthday, setBirthday] = useState('');
  const [adding, setAdding] = useState(false);

  // 연인/친구 모드
  const [registeredList, setRegisteredList] = useState([]);

  const formatPhone = (phone) => {
    if (!phone) return '';
    // 8자리 (뒤 8자리만) → 010 prefix 추가
    if (phone.length === 8) return '010-' + phone.slice(0, 4) + '-' + phone.slice(4);
    // 11자리 (전체 번호)
    if (phone.length === 11) return phone.slice(0, 3) + '-' + phone.slice(3, 7) + '-' + phone.slice(7);
    return phone;
  };

  const maskPhone = (phone) => {
    if (!phone || phone.length < 8) return phone || '-';
    return phone.slice(0, 3) + '-****-' + phone.slice(-4);
  };

  // 유형 선택
  const handleSelectMode = (m) => {
    setMode(m);
    setStep('phone');
    setPhoneInput('');
    setError(null);
    setResult(null);
  };

  // 전화번호 입력 후 확인
  const handlePhoneNext = async () => {
    if (phoneInput.length !== 8) return;
    const fullPhone = '010' + phoneInput;

    setLoading(true);
    setError(null);

    try {
      // 이미 가입된 회원인지 확인
      const res = await getMemberByPhone(fullPhone);
      if (res.member) {
        if (mode === 'family') {
          // 가족 모드: 이미 회원이면 바로 자녀 추가 화면으로
          setParentMember({ ...res.member, phone: res.member.phone || fullPhone });
          await loadFamilyData(res.member);
          setStep('children');
          setResult({ success: true, message: '이미 가입된 회원입니다. 자녀를 추가해 주세요.' });
        } else {
          // 연인/친구 모드: 이미 가입됨
          setError('이미 가입된 번호입니다: ' + maskPhone(fullPhone));
        }
      } else {
        // API 성공이지만 회원 없음 → 확인 화면으로
        setStep('confirm');
      }
    } catch (err) {
      // 404(회원 없음)만 신규 가입 진행, 그 외는 에러 표시
      if (err?.status === 404 || err?.error_code === 'MEMBER_NOT_FOUND') {
        setStep('confirm');
      } else {
        setError('서버 연결에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 가입 실행
  const handleRegister = async () => {
    const fullPhone = '010' + phoneInput;
    setLoading(true);
    setError(null);

    try {
      const storeId = localStorage.getItem('store_id');
      const res = await registerMemberOnly(fullPhone, storeId);
      const member = res.member;

      if (mode === 'family') {
        setParentMember({ ...member, phone: member.phone || fullPhone });
        await loadFamilyData(member);
        setStep('children');
        setResult({ success: true, message: '멤버십 가입 완료! 자녀를 추가해 주세요.' });
      } else {
        // 연인/친구 모드
        setRegisteredList(prev => [...prev, { phone: fullPhone, member_id: member.member_id }]);
        setStep('friend-done');
        setResult({ success: true, message: `${formatPhone(fullPhone)} 가입 완료!` });
      }
    } catch (err) {
      setError(err.message || '가입에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 번호 수정
  const handleFixPhone = () => {
    setStep('phone');
    setError(null);
  };

  // 가족 데이터 로드
  const loadFamilyData = async (memberData) => {
    try {
      const res = await getFamilyMembers(memberData.member_id);
      if (res.success) {
        setFamilyData(res);
      } else {
        setFamilyData({
          parent: { member_id: memberData.member_id, name: memberData.name, phone: memberData.phone, balance: memberData.balance || 0 },
          children: [], children_count: 0, total_family_balance: memberData.balance || 0,
        });
      }
    } catch {
      setFamilyData({
        parent: { member_id: memberData.member_id, name: memberData.name, phone: memberData.phone, balance: memberData.balance || 0 },
        children: [], children_count: 0, total_family_balance: memberData.balance || 0,
      });
    }
  };

  // 자녀 추가
  const handleAddChild = async () => {
    if (!nickname.trim()) return;
    const parentId = familyData?.parent?.member_id || parentMember.member_id;

    setAdding(true);
    setResult(null);

    try {
      const res = await addChildMember(parentId, {
        nickname: nickname.trim(),
        name: null,
        birthday: birthday || null,
      });
      if (res.success) {
        setResult({ success: true, message: `자녀 '${nickname.trim()}' 추가 완료!` });
        setNickname('');
        setBirthday('');
        await loadFamilyData(parentMember);
      }
    } catch (err) {
      setResult({ success: false, message: err.message || '자녀 추가에 실패했습니다' });
    } finally {
      setAdding(false);
    }
  };

  // 연인/친구 - 다음 사람 등록
  const handleNextFriend = () => {
    setPhoneInput('');
    setError(null);
    setResult(null);
    setStep('phone');
  };

  // 전체 초기화
  const handleReset = () => {
    setMode(null);
    setStep('type');
    setPhoneInput('');
    setLoading(false);
    setError(null);
    setResult(null);
    setParentMember(null);
    setFamilyData(null);
    setNickname('');
    setBirthday('');
    setAdding(false);
    setRegisteredList([]);
  };

  // 넘버패드 컴포넌트
  const Numpad = ({ value, onChange, onClear, disabled }) => (
    <div className="mr-numpad">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
        <button
          key={n}
          type="button"
          className="mr-numpad-btn"
          disabled={disabled}
          onClick={() => value.length < 8 && onChange(value + n)}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        className="mr-numpad-btn mr-numpad-clear"
        disabled={disabled}
        onClick={onClear}
      >
        전체삭제
      </button>
      <button
        type="button"
        className="mr-numpad-btn"
        disabled={disabled}
        onClick={() => value.length < 8 && onChange(value + '0')}
      >
        0
      </button>
      <button
        type="button"
        className="mr-numpad-btn mr-numpad-backspace"
        disabled={disabled}
        onClick={() => onChange(value.slice(0, -1))}
      >
        ⌫
      </button>
    </div>
  );

  return (
    <div className="mr-page">
      {/* 헤더 */}
      <header className="mr-header">
        <button className="btn-back" onClick={() => step === 'type' ? navigate('/') : handleReset()}>
          ← {step === 'type' ? '메뉴' : '처음으로'}
        </button>
        <div className="mr-header-center" onClick={() => {
          // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>📝 멤버십 가입</h1>
          <StoreBadge />
        </div>
        <div className="mr-header-spacer" />
      </header>

      <main className="mr-content">
        {/* 1. 유형 선택 */}
        {step === 'type' && (
          <div className="mr-type-section">
            <p className="mr-type-title">가입 유형을 선택해 주세요</p>
            <div className="mr-type-cards">
              <button className="mr-type-card mr-type-family" onClick={() => handleSelectMode('family')}>
                <span className="mr-type-icon">👨‍👩‍👧‍👦</span>
                <h2>가족</h2>
                <p>대표 번호 등록 후<br />자녀를 추가합니다</p>
              </button>
              <button className="mr-type-card mr-type-friends" onClick={() => handleSelectMode('friends')}>
                <span className="mr-type-icon">👫</span>
                <h2>연인 / 친구</h2>
                <p>한 명씩 개별로<br />등록합니다</p>
              </button>
            </div>
          </div>
        )}

        {/* 2. 전화번호 입력 */}
        {step === 'phone' && !loading && (
          <div className="mr-phone-section">
            <div className="mr-mode-badge">
              {mode === 'family' ? '👨‍👩‍👧‍👦 가족' : '👫 연인/친구'}
              {mode === 'friends' && registeredList.length > 0 && (
                <span className="mr-mode-count"> ({registeredList.length}명 등록됨)</span>
              )}
            </div>
            <div className="mr-phone-card">
              <p className="mr-phone-label">
                {mode === 'family' ? '대표 전화번호 입력' : `${registeredList.length + 1}번째 전화번호 입력`}
              </p>
              <div className="mr-phone-display">
                <span style={{color: '#999'}}>010</span>
                {phoneInput.length > 0 ? `-${phoneInput.slice(0, 4)}` : '-'}
                {phoneInput.length > 4 ? `-${phoneInput.slice(4)}` : ''}
                {phoneInput.length === 0 && <span style={{color: '#bbb'}}> 뒤 8자리 입력</span>}
              </div>
              {error && <div className="mr-phone-error">{error}</div>}
              <Numpad
                value={phoneInput}
                onChange={(v) => { setError(null); setPhoneInput(v); }}
                onClear={() => { setError(null); setPhoneInput(''); }}
                disabled={false}
              />
              <button
                className="mr-phone-btn"
                onClick={handlePhoneNext}
                disabled={phoneInput.length !== 8}
              >
                다음
              </button>
            </div>
            {mode === 'friends' && registeredList.length > 0 && (
              <button className="mr-btn-complete" onClick={handleReset}>
                등록 완료 ({registeredList.length}명)
              </button>
            )}
          </div>
        )}

        {/* 3. 번호 확인 */}
        {step === 'confirm' && !loading && (
          <div className="mr-confirm-section">
            <div className="mr-confirm-card">
              <span className="mr-confirm-icon">📱</span>
              <h2 className="mr-confirm-title">전화번호를 확인해 주세요</h2>
              <p className="mr-confirm-desc">
                아래 번호로 멤버십에 가입합니다.<br />
                번호가 맞는지 다시 확인해 주세요.
              </p>
              <div className="mr-confirm-phone">
                {formatPhone(phoneInput)}
              </div>
              {error && <div className="mr-result-msg fail">❌ {error}</div>}
              <div className="mr-confirm-buttons">
                <button className="mr-btn-fix" onClick={handleFixPhone}>
                  번호 수정
                </button>
                <button className="mr-btn-register" onClick={handleRegister}>
                  가입 진행
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="mr-loading">
            <div className="mr-spinner" />
            <p>처리 중...</p>
          </div>
        )}

        {/* 4. 가족 - 자녀 추가 */}
        {step === 'children' && !loading && parentMember && familyData && (
          <div className="mr-children-section">
            {/* 결과 메시지 */}
            {result && (
              <div className={`mr-result-msg ${result.success ? 'success' : 'fail'}`}>
                {result.success ? '✅' : '❌'} {result.message}
              </div>
            )}

            <div className="mr-children-row">
              {/* 좌: 대표 + 자녀 목록 */}
              <div className="mr-children-left">
                <div className="mr-parent-card">
                  <div className="mr-parent-header">
                    <span className="mr-parent-avatar">👤</span>
                    <div>
                      <h2 className="mr-parent-name">{parentMember.name || maskPhone(parentMember.phone)}</h2>
                      <span className="mr-parent-phone">{parentMember.name ? maskPhone(parentMember.phone) : ''}</span>
                    </div>
                    <span className="mr-parent-badge">대표</span>
                  </div>
                </div>

                {familyData.children.length > 0 && (
                  <div className="mr-child-list-card">
                    <h3 className="mr-child-list-title">
                      👧👦 등록된 자녀 ({familyData.children.length}명)
                    </h3>
                    <div className="mr-child-list">
                      {familyData.children.map((child, idx) => (
                        <div key={child.member_id || idx} className="mr-child-item">
                          <div className="mr-child-avatar">{child.nickname?.charAt(0) || '자'}</div>
                          <div className="mr-child-info">
                            <span className="mr-child-nickname">{child.nickname}</span>
                            <span className="mr-child-balance">🪙 {(child.balance || 0).toLocaleString()}개</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 우: 자녀 추가 폼 + 완료 */}
              <div className="mr-children-right">
                <div className="mr-add-child-form">
                  <h3 className="mr-form-title">➕ 자녀 추가</h3>
                  <div className="mr-form-group">
                    <label>닉네임 *</label>
                    <input
                      type="text"
                      placeholder="예: 첫째, 둘째, 자녀1"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      maxLength={50}
                      disabled={adding}
                    />
                  </div>
                  <div className="mr-form-group">
                    <label>생일 (선택)</label>
                    <input
                      type="date"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                      disabled={adding}
                    />
                  </div>
                  <button
                    className="mr-btn-add-child"
                    onClick={handleAddChild}
                    disabled={adding || !nickname.trim()}
                  >
                    {adding ? '추가 중...' : '자녀 추가'}
                  </button>
                </div>

                <button className="mr-btn-complete" onClick={handleReset}>
                  등록 완료
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 5. 연인/친구 - 1명 등록 완료 */}
        {step === 'friend-done' && !loading && (
          <div className="mr-friend-done-section">
            <div className="mr-friend-done-card">
              <span className="mr-friend-done-icon">🎉</span>
              {result && (
                <div className={`mr-result-msg ${result.success ? 'success' : 'fail'}`}>
                  {result.success ? '✅' : '❌'} {result.message}
                </div>
              )}
              <p className="mr-friend-done-count">
                현재 <strong>{registeredList.length}명</strong> 등록 완료
              </p>
              <div className="mr-friend-done-buttons">
                <button className="mr-btn-next-friend" onClick={handleNextFriend}>
                  ➕ 다음 사람 등록
                </button>
                <button className="mr-btn-complete" onClick={handleReset}>
                  등록 완료
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MembershipRegisterPage;
